import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import usePromoStatus from "@/lib/usePromoStatus";

/**
 * Upgrade offer banner shown on the Dashboard for Basic-tier members.
 * Combines the loyalty upgrade ($750 instead of $1,250) with the live
 * referral progress. Once the referral coupon unlocks, the price drops
 * to $600 (20% off the loyalty price) and the CTA turns emerald.
 */
export default function UpgradeOfferBanner({ user }) {
  const [stats, setStats] = useState(null);
  const { t } = useTranslation();
  const { lp, referral, loyaltyOn, referralOn, upgradeAmt, params: P, m } = usePromoStatus();

  useEffect(() => {
    if (!user || user.tier !== "basic") return;
    api.get("/referrals/me").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (!user || user.tier !== "basic") return null;

  const loyaltyPrice = upgradeAmt;
  const referralDiscount = stats?.discount_percent ?? referral.percent;
  const finalPrice = stats?.coupon_unlocked && referralOn
    ? Math.round(loyaltyPrice * (1 - referralDiscount / 100) * 100) / 100
    : loyaltyPrice;

  const progressPct = stats
    ? Math.min(100, Math.round((stats.referred_activated / stats.required) * 100))
    : 0;

  const unlocked = !!stats?.coupon_unlocked && referralOn;

  return (
    <section
      data-testid="dashboard-upgrade-banner"
      className={`relative overflow-hidden rounded-2xl text-white border ${
        unlocked ? "bg-emerald-900 border-emerald-500/40" : "bg-slate-950 border-amber-500/25"
      }`}
    >
      <div className={`absolute inset-0 opacity-30 ${
        unlocked
          ? "bg-[radial-gradient(circle_at_20%_30%,rgba(16,185,129,0.6),transparent_55%),radial-gradient(circle_at_80%_80%,rgba(245,158,11,0.35),transparent_55%)]"
          : "bg-[radial-gradient(circle_at_15%_20%,rgba(30,64,175,0.55),transparent_55%),radial-gradient(circle_at_85%_80%,rgba(245,158,11,0.35),transparent_55%)]"
      }`} />

      <div className="relative p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
        {/* Copy column */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-bold rounded-full px-3 py-1.5 border bg-white/10 border-white/20">
            {unlocked ? <Trophy size={11} className="text-emerald-300" /> : <Sparkles size={11} className="text-amber-400" />}
            {unlocked ? "Coupon unlocked" : "Upgrade offer · Basic member"}
          </div>
          <h2 className="mt-4 font-display font-black text-2xl sm:text-3xl tracking-tighter leading-tight">
            {unlocked
              ? `Your Premium upgrade is ${m(finalPrice)}.`
              : loyaltyOn ? "Upgrade to Premium — pay only the delta." : "Upgrade to Premium."}
          </h2>
          <p className="mt-3 text-sm text-slate-300 max-w-xl leading-relaxed">
            {unlocked
              ? `${referral.required} activated referrals — a ${referralDiscount}% coupon on top of the loyalty price. Applied automatically at checkout.`
              : loyaltyOn
                ? `Existing Basic members upgrade for ${m(loyaltyPrice)} instead of ${m(lp.premium)} — applied automatically at checkout.`
                : `Upgrade to Premium for ${m(lp.premium)} and unlock everything.`}
          </p>

          {/* Loud referral savings sticker — third Basic→Premium touchpoint */}
          {!unlocked && referralOn && (
            <div className="mt-4 relative overflow-hidden rounded-lg border-2 border-amber-400 bg-amber-500/10 backdrop-blur-sm p-3 max-w-xl">
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-amber-400/25 blur-2xl pointer-events-none" />
              <div className="relative flex items-start gap-2.5">
                <span className="inline-flex w-7 h-7 rounded-md bg-amber-400 text-slate-950 items-center justify-center shrink-0">
                  <Sparkles size={14} strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-black text-amber-300 leading-tight">
                    {t("pricingUi.referralStack.eyebrow", P)}
                  </div>
                  <div className="mt-0.5 text-sm font-bold text-white leading-snug">
                    {t("pricingUi.referralStack.description", P)}{" "}
                    <span className="text-amber-300">{t("pricingUi.referralStack.outcome", P)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {stats && referralOn && (
            <div className="mt-5 max-w-md">
              <div className="flex items-baseline justify-between mb-1.5 text-xs text-slate-300">
                <span>{stats.referred_activated} of {stats.required} friends activated</span>
                <span>{stats.remaining > 0 ? `${stats.remaining} to go` : "You did it"}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    unlocked ? "bg-emerald-400" : "bg-gradient-to-r from-blue-400 to-amber-400"
                  }`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Price + CTA column */}
        <div className="lg:col-span-1 lg:pl-6 lg:border-l lg:border-white/10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 font-semibold">
            Your price
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            {(loyaltyOn || unlocked) && <span className="text-slate-500 line-through font-semibold text-xl">{m(lp.premium)}</span>}
            <span className={`font-display font-black text-4xl sm:text-5xl tracking-tighter ${
              unlocked ? "text-emerald-300" : "text-white"
            }`}>
              {m(finalPrice)}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-400">
            {unlocked
              ? `${loyaltyOn ? `Loyalty ${m(loyaltyPrice)} · then ` : ""}${referralDiscount}% off (–${m(loyaltyPrice - finalPrice)})`
              : [loyaltyOn && `Save ${m(lp.premium - loyaltyPrice)} today`, referralOn && `save ${m(loyaltyPrice * referral.percent / 100)} ${loyaltyOn ? "more " : ""}with ${referral.required} referrals`].filter(Boolean).join(" · ")}
          </div>

          <Link
            to="/pricing"
            data-testid="dashboard-upgrade-cta"
            className={`mt-5 inline-flex items-center gap-2 w-full justify-center text-sm font-semibold px-5 py-3 rounded-md transition-colors ${
              unlocked
                ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                : "bg-amber-500 hover:bg-amber-400 text-slate-950"
            }`}
          >
            {unlocked ? "Claim your discount" : "See upgrade details"}
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
